import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Boxes, ShieldCheck, AlertTriangle, Clock, Pencil, Trash2, Plus, X } from "lucide-react";

const BOUNDARY_LABEL = {
  s4_core: "S/4 core", successfactors: "SuccessFactors", ecp: "EC Payroll",
  sales_cloud: "Sales Cloud", service_cloud: "Service Cloud", revenue_cloud: "Revenue Cloud",
  data_cloud: "Data Cloud", marketing_cloud: "Marketing Cloud", experience_cloud: "Experience Cloud",
  out_of_scope: "Out of scope",
};
const STATUS_STYLE = {
  verified: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: ShieldCheck, label: "SME-verified" },
  under_review: { cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Clock, label: "Under review" },
  illustrative: { cls: "bg-zinc-100 text-zinc-600 border-zinc-300", Icon: AlertTriangle, label: "Illustrative" },
};
const EFFORT_STYLE = { low: "text-emerald-600", medium: "text-amber-600", high: "text-red-600" };

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.illustrative;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${s.cls}`}
          data-testid={`erp-status-${status}`}>
      <s.Icon size={10} /> {s.label}
    </span>
  );
}

export default function ErpBlueprintsPanel({ isOwner }) {
  const [meta, setMeta] = useState(null);
  const [pkg, setPkg] = useState("sap_s4hana");
  const [rows, setRows] = useState(null);
  const [cov, setCov] = useState(null);
  const [edit, setEdit] = useState(null);   // mapping being edited (or {} for new)

  const load = useCallback(() => {
    api.get(`/admin/erp/mappings?package=${pkg}`).then(({ data }) => setRows(data)).catch(() => setRows({ mappings: [] }));
    api.get(`/admin/erp/coverage?package=${pkg}`).then(({ data }) => setCov(data)).catch(() => {});
  }, [pkg]);
  useEffect(() => {
    api.get("/admin/erp/packages").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (m, status) => {
    try {
      await api.post(`/admin/erp/mappings/${m.id}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };
  const del = async (m) => {
    try { await api.delete(`/admin/erp/mappings/${m.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const pkgLabel = meta?.packages?.[pkg]?.label || "Blueprint";

  return (
    <div className="ek-admin space-y-6" data-testid="erp-blueprints-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Boxes size={20} /> Application blueprints</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            {pkgLabel} blueprints overlaid on the value-stream spine (SAP, Salesforce…). Owned & maintained,
            release-versioned, and governed — mappings are <strong>illustrative until an accredited SME verifies them</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={pkg} onChange={(e) => setPkg(e.target.value)} data-testid="erp-package-select"
                  className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface">
            {meta && Object.entries(meta.packages).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => setEdit({ package: pkg })} data-testid="erp-new-mapping-btn"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm bg-[#7737F6] text-white rounded-lg">
            <Plus size={15} /> New mapping
          </button>
        </div>
      </div>

      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 flex items-center gap-2" data-testid="erp-disclaimer">
        <AlertTriangle size={14} /> {rows?.disclaimer || meta?.disclaimers?.[pkg] || meta?.disclaimer || "Advisory only — mappings are indicative until SME-verified."}
      </div>

      {cov && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="erp-coverage">
          <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Mappings</div><div className="text-2xl font-semibold text-[#7737F6] tabular-nums">{cov.total}</div></div>
          <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">SME-verified</div><div className="text-2xl font-semibold text-emerald-600 tabular-nums">{cov.verified}</div></div>
          <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Streams covered</div><div className="text-2xl font-semibold ek-text tabular-nums">{cov.streams.length}</div></div>
          <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Awaiting verify</div><div className="text-2xl font-semibold text-amber-600 tabular-nums">{cov.total - cov.verified}</div></div>
        </div>
      )}

      {rows === null ? (
        <div className="ek-text-muted text-sm py-8">Loading…</div>
      ) : !rows.mappings?.length ? (
        <div className="border ek-line ek-surface-2 rounded-lg p-8 text-center ek-text-muted text-sm" data-testid="erp-empty">
          No mappings for this package yet.
        </div>
      ) : (
        <div className="border ek-line ek-surface rounded-lg divide-y divide-zinc-100">
          {rows.mappings.map((m) => (
            <div key={m.id} className="p-4" data-testid={`erp-row-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-medium ek-text">{m.title || m.node_id}</span>
                    {m.scope_item_id ? <span className="text-[10.5px] font-mono bg-[#7737F6]/10 text-[#7737F6] px-1.5 py-0.5 rounded" data-testid="erp-scope-item">{m.scope_item_id}</span> : null}
                    <StatusPill status={m.status} />
                    <span className="text-[10.5px] ek-text-muted border ek-line rounded px-1.5 py-0.5">{BOUNDARY_LABEL[m.boundary] || m.boundary}</span>
                    {m.sunset ? <span className="text-[10.5px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" data-testid="erp-sunset">sunset {m.sunset}</span> : null}
                  </div>
                  <div className="text-[12px] ek-text-muted mt-1">
                    {m.stream_id} · {m.node_id} · {m.lob} · effort <span className={EFFORT_STYLE[m.reconfig_effort]}>{m.reconfig_effort}</span>
                    {m.fiori_apps?.length ? ` · ${pkg.startsWith("sap") ? "Fiori" : "Apps"}: ${m.fiori_apps.join(", ")}` : ""}
                  </div>
                  {m.standard_practice ? <div className="text-[12.5px] ek-text-soft mt-2"><strong>Standard:</strong> {m.standard_practice}</div> : null}
                  {m.greenfield_note ? <div className="text-[12px] ek-text-soft mt-1"><strong>Greenfield:</strong> {m.greenfield_note}</div> : null}
                  {m.brownfield_note ? <div className="text-[12px] ek-text-soft mt-1"><strong>Brownfield:</strong> {m.brownfield_note}</div> : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEdit(m)} data-testid={`erp-edit-${m.id}`} className="ek-text-muted hover:text-[#7737F6]"><Pencil size={15} /></button>
                  <button onClick={() => del(m)} data-testid={`erp-delete-${m.id}`} className="ek-text-muted hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {m.status !== "under_review" && (
                  <button onClick={() => setStatus(m, "under_review")} data-testid={`erp-review-${m.id}`}
                          className="text-[11px] px-2 py-1 border ek-line rounded hover:bg-zinc-50">Send for review</button>
                )}
                {m.status !== "verified" && (
                  <button onClick={() => setStatus(m, "verified")} disabled={!isOwner} data-testid={`erp-verify-${m.id}`}
                          title={isOwner ? "" : "Only an accredited owner can verify"}
                          className="text-[11px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-40">
                    Mark SME-verified
                  </button>
                )}
                {m.status === "verified" && <span className="text-[11px] ek-text-muted">Verified by {m.verified_by}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <MappingEditor pkg={pkg} meta={meta} mapping={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function MappingEditor({ pkg, meta, mapping, onClose, onSaved }) {
  const isNew = !mapping.id;
  const editorPkg = mapping.package || pkg;
  const isSap = editorPkg.startsWith("sap");
  const pkgLabel = meta?.packages?.[editorPkg]?.label || "";
  const pkgBoundaries = meta?.package_boundaries?.[editorPkg] || Object.keys(BOUNDARY_LABEL);
  const [streams, setStreams] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [f, setF] = useState({
    package: mapping.package || pkg, release: mapping.release || "",
    stream_id: mapping.stream_id || "", node_id: mapping.node_id || "", node_level: mapping.node_level || "L2",
    scope_item_id: mapping.scope_item_id || "", title: mapping.title || "",
    standard_practice: mapping.standard_practice || "", greenfield_note: mapping.greenfield_note || "",
    brownfield_note: mapping.brownfield_note || "", reconfig_effort: mapping.reconfig_effort || "medium",
    fiori_apps: (mapping.fiori_apps || []).join(", "), lob: mapping.lob || "",
    boundary: mapping.boundary || pkgBoundaries[0] || "out_of_scope", sunset: mapping.sunset || "", provenance: mapping.provenance || "",
  });
  useEffect(() => { api.get("/admin/erp/streams").then(({ data }) => setStreams(data.streams || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.stream_id) { setNodes([]); return; }
    api.get(`/admin/erp/streams/${f.stream_id}/nodes`).then(({ data }) => setNodes(data.nodes || [])).catch(() => setNodes([]));
  }, [f.stream_id]);

  const save = async () => {
    if (!f.stream_id || !f.node_id) return toast.error("Pick a value stream and node");
    const payload = { ...f, fiori_apps: f.fiori_apps.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (isNew) await api.post("/admin/erp/mappings", payload);
      else await api.put(`/admin/erp/mappings/${mapping.id}`, payload);
      toast.success(isNew ? "Mapping created" : "Mapping saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const field = (label, key, ph = "") => (
    <label className="text-[12px] ek-text-soft block">{label}
      <input value={f[key]} onChange={(e) => setF((s) => ({ ...s, [key]: e.target.value }))} placeholder={ph}
             data-testid={`erp-field-${key}`} className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" />
    </label>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="erp-editor">
      <div className="ek-surface rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="ek-heading text-lg">{isNew ? "New" : "Edit"} {pkgLabel} mapping</h3>
          <button onClick={onClose} data-testid="erp-editor-close"><X size={18} className="ek-text-muted" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-[12px] ek-text-soft block">Value stream
            <select value={f.stream_id} onChange={(e) => setF((s) => ({ ...s, stream_id: e.target.value, node_id: "" }))}
                    data-testid="erp-field-stream" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
              <option value="">Select…</option>
              {streams.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="text-[12px] ek-text-soft block">Node (L2/L3)
            <select value={f.node_id} onChange={(e) => { const n = nodes.find((x) => x.node_id === e.target.value); setF((s) => ({ ...s, node_id: e.target.value, node_level: n?.level || "L2" })); }}
                    data-testid="erp-field-node" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
              <option value="">Select…</option>
              {nodes.map((n) => <option key={n.node_id} value={n.node_id}>{n.level} · {n.name}</option>)}
            </select>
          </label>
          {field(isSap ? "SAP scope item (ID)" : "Feature / scope ref", "scope_item_id", isSap ? "e.g. J59" : "e.g. SC-OPP")}
          {field("Title", "title")}
          <label className="text-[12px] ek-text-soft block">Boundary
            <select value={f.boundary} onChange={(e) => setF((s) => ({ ...s, boundary: e.target.value }))}
                    data-testid="erp-field-boundary" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
              {pkgBoundaries.map((k) => <option key={k} value={k}>{BOUNDARY_LABEL[k] || k}</option>)}
            </select>
          </label>
          <label className="text-[12px] ek-text-soft block">Reconfig effort
            <select value={f.reconfig_effort} onChange={(e) => setF((s) => ({ ...s, reconfig_effort: e.target.value }))}
                    data-testid="erp-field-effort" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </label>
          {field("LoB / module", "lob", isSap ? "e.g. FIN, PP" : "e.g. Sales, Service")}
          {field("Sunset (e.g. 2030)", "sunset")}
          {field(isSap ? "Fiori apps (comma-sep)" : "Apps / features (comma-sep)", "fiori_apps", isSap ? "" : "e.g. Sales Cloud, CPQ")}
        </div>
        <label className="text-[12px] ek-text-soft block">Standard practice
          <textarea value={f.standard_practice} onChange={(e) => setF((s) => ({ ...s, standard_practice: e.target.value }))}
                    rows={2} data-testid="erp-field-standard" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
        <label className="text-[12px] ek-text-soft block">Greenfield note
          <textarea value={f.greenfield_note} onChange={(e) => setF((s) => ({ ...s, greenfield_note: e.target.value }))}
                    rows={2} data-testid="erp-field-greenfield" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
        <label className="text-[12px] ek-text-soft block">Brownfield note
          <textarea value={f.brownfield_note} onChange={(e) => setF((s) => ({ ...s, brownfield_note: e.target.value }))}
                    rows={2} data-testid="erp-field-brownfield" className="mt-1 border ek-line rounded-lg px-3 py-2 text-sm ek-surface w-full" /></label>
        <button onClick={save} data-testid="erp-editor-save" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg w-full">
          {isNew ? "Create mapping" : "Save mapping"}
        </button>
      </div>
    </div>
  );
}
